import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries } from "@/entities/dossier";
import { ApiError, useApiClient } from "@/infrastructure/rest";
import { HerinneringEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { HerinneringDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { HERINNERINGEN_QUERY_KEY } from "./constants.ts";
import { Herinnering } from "./domain.ts";

export function useGetHerinneringen<T = Herinnering[]>(
    dossierId: string,
    options?: Omit<UseQueryOptions<Herinnering[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, HERINNERINGEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<HerinneringDto[]>(`/dossiers/${dossierId}/herinneringen`);
            return data?.map((dto) => Herinnering.fromDto(dto))?.sort(Herinnering.sort) ?? [];
        },
        ...options,
    });
}

export type PostHerinnering = {
    dossierId: string;
    tekst: string;
    scheduledAt: Date;
};

export function usePostHerinnering() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...body }: PostHerinnering) => {
            try {
                await apiClient.post(`/dossiers/${dossierId}/herinneringen`, body);
            } catch (e) {
                // todo make this more generic?
                if (e instanceof ApiError) {
                    throw new Error("Fout bij het aanmaken van de herinnering");
                }
            }
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting herinnering", error);
        },
    });
}

export type DeleteHerinnering = {
    dossierId: string;
    herinneringId: number;
};

export function useDeleteHerinnering() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, herinneringId }: DeleteHerinnering) => {
            await apiClient.delete(`/dossiers/${dossierId}/herinneringen/${herinneringId}`);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error deleting herinnering", error);
        },
    });
}

export function useRealtimeHerinneringen() {
    const queryClient = useQueryClient();

    useRealtimeTopic<HerinneringEvent>("herinneringen", {
        onMessage: (message) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIERS_QUERY_KEY, String(message.dossierId), HERINNERINGEN_QUERY_KEY],
            });
        },
    });
}
